/**
 * Loading, error, unavailable and empty.
 *
 * FOUR STATES, NOT THREE. "Unavailable" is separate from "error" on purpose:
 * an endpoint that does not exist yet is not a failure, and rendering it in
 * the same red-adjacent register as a broken request teaches people to
 * ignore both. The brief makes the same argument about refusals — a refusal
 * rendered as an error gets worked around rather than fixed.
 */

import type { ReactNode } from 'react';
import { NotAvailable } from '../api';
import './shell.css';

export function Loading({ what }: { what: string }) {
  return (
    <div className="pem-state" role="status">
      <span className="pem-label">Loading {what}</span>
    </div>
  );
}

/** Something genuinely went wrong. */
export function Failed({ what, error }: { what: string; error: unknown }) {
  const detail = error instanceof Error ? error.message : String(error);
  return (
    <div className="pem-state pem-state--failed" role="alert">
      <span className="pem-label">Could not load {what}</span>
      <p className="pem-state__detail">{detail}</p>
    </div>
  );
}

/**
 * The backend cannot answer this yet. Occupies the place the answer would
 * occupy, names what is missing, and does not invent the number.
 */
export function Unavailable({ error }: { error: NotAvailable }) {
  return (
    <div className="pem-state pem-state--unavailable">
      <span className="pem-badge pem-badge--caution">Not available</span>
      <p className="pem-state__detail">
        <strong>{error.what}</strong> — {error.why}
      </p>
    </div>
  );
}

/** Routes an unknown error to the right one of the two. */
export function ErrorState({ what, error }: { what: string; error: unknown }) {
  return error instanceof NotAvailable
    ? <Unavailable error={error} />
    : <Failed what={what} error={error} />;
}

/**
 * Nothing here yet — and that is a legitimate product state, not a
 * placeholder. It offers the next action and NEVER fabricates a statistic to
 * fill the space.
 */
export function Empty(
  { title, body, action }: { title: string; body: string; action?: ReactNode },
) {
  return (
    <div className="pem-state pem-state--empty">
      <h2>{title}</h2>
      <p className="pem-state__detail">{body}</p>
      {action}
    </div>
  );
}
